import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { collection, query, where, getDocs, limit } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { generateUniqueSlug } from '../utils/slugify'

export default function FeaturedTours() {
  const { t, i18n } = useTranslation()
  const [tours, setTours] = useState([])
  const [loading, setLoading] = useState(true)

  const currentLang = i18n.language || 'vi'

  // Helper function to get translated tour data
  const getTourName = (tour) => {
    if (currentLang !== 'vi' && tour.translations && tour.translations[currentLang]) {
      return tour.translations[currentLang].name || tour.name
    }
    return tour.name
  }

  const getTourLocation = (tour) => {
    if (currentLang !== 'vi' && tour.translations && tour.translations[currentLang]) {
      return tour.translations[currentLang].location || tour.location
    }
    return tour.location
  }

  useEffect(() => {
    loadFeaturedTours()
  }, [])

  const loadFeaturedTours = async () => {
    try {
      const q = query(
        collection(db, 'TOURS'),
        where('featured', '==', true),
        where('approved', '==', true)
      )
      const snapshot = await getDocs(q)
      const toursData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(tour => tour.disabled !== true) // Filter in code
        .slice(0, 6) // Limit to 6
      setTours(toursData)
    } catch (error) {
      console.error('Error loading featured tours:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <section className="featured-tours">
        <div className="container">
          <h2>{t('home.featured.title')}</h2>
          <div className="loading">{t('common.loading')}</div>
        </div>
      </section>
    )
  }

  return (
    <section className="featured-tours">
      <div className="container">
        <h2>🏖️ {t('home.featured.title')}</h2>
        <div className="tours-grid">
          {tours.map(tour => {
            const tourSlug = generateUniqueSlug(tour.name, tour.id)
            return (
              <Link key={tour.id} to={`/tours/${tourSlug}`} className="tour-card">
                <div className="tour-image">
                  <img src={tour.imageUrl || 'https://picsum.photos/400/300'} alt={tour.name} />
                </div>
                <div className="tour-info">
                  <h3>{getTourName(tour)}</h3>
                  <p className="location">📍 {getTourLocation(tour)}</p>
                  <p className="duration">⏰ {tour.duration}</p>
                  <div className="tour-footer">
                    <span className="price">{tour.price?.toLocaleString('vi-VN')}đ</span>
                    <span className="view-detail">{t('common.viewDetails')} →</span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
        {tours.length === 0 && (
          <p className="no-tours">{t('home.featured.subtitle')}</p>
        )}
      </div>

      <style>{`
        .featured-tours {
          padding: 80px 24px;
          background: #f9fafb;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .featured-tours h2 {
          text-align: center;
          font-size: 2.5rem;
          margin-bottom: 48px;
          color: #1f2937;
        }

        .tours-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 32px;
        }

        .tour-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          text-decoration: none;
          color: inherit;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.3s;
        }

        .tour-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .tour-image {
          height: 240px;
          overflow: hidden;
        }

        .tour-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        .tour-card:hover .tour-image img {
          transform: scale(1.1);
        }

        .tour-info {
          padding: 24px;
        }

        .tour-info h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: #1f2937;
        }

        .location, .duration {
          color: #6b7280;
          margin-bottom: 8px;
        }

        .tour-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .price {
          font-size: 1.25rem;
          font-weight: 700;
          color: #059669;
        }

        .view-detail {
          color: #667eea;
          font-weight: 600;
        }

        .loading, .no-tours {
          text-align: center;
          padding: 60px;
          color: #6b7280;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .tours-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  )
}

